import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import Reveal from "@/components/Reveal";

const FILMS = [
  {
    key: "bts",
    webm: "/media/craft-film.webm",
    mp4: "/media/craft-film.mp4",
    poster: "/media/craft-poster.jpg",
    eyebrow: "On Set",
    label: "Behind the Scenes",
    alt: "Behind the scenes on a Brand New Money Films production",
    hasSound: false,
  },
  {
    key: "final",
    webm: "/media/final-film.webm",
    mp4: "/media/final-film.mp4",
    poster: "/media/final-poster.jpg",
    eyebrow: "Final Film",
    label: "Bluestone Campaign",
    alt: "The final Bluestone campaign film by Brand New Money Films",
    hasSound: true,
  },
];

const SOUND_PREF_KEY = "bnm.craft.sound";

// Behind the Craft. A diptych: how we work, and what we create.
// Both films autoplay muted while in view; the Final Film carries its
// original soundtrack behind a quiet "Tap for sound" invitation.
export const CraftFilm = () => {
  const sectionRef = useRef(null);
  const finalRef = useRef(null);
  const triedUnmutedRef = useRef(false);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return sessionStorage.getItem(SOUND_PREF_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const root = sectionRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return undefined;
    const videos = root.querySelectorAll("video");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          videos.forEach((v) => {
            if (e.isIntersecting) {
              const isFinal = v === finalRef.current;
              if (isFinal && soundOn) v.muted = false;
              if (isFinal && !soundOn && !triedUnmutedRef.current) {
                // Autoplay with audio where the browser allows it,
                // fall back to muted without interrupting playback.
                    triedUnmutedRef.current = true;
                    v.muted = false;
                    v.play()
                      .then(() => {
                        setSoundOn(true);
                        // Storage may be unavailable in private mode; preference is optional.
                        try { sessionStorage.setItem(SOUND_PREF_KEY, "1"); } catch { /* storage unavailable */ }
                      })
                      .catch(() => {
                        // Browser autoplay policy blocked sound; fall back to muted playback.
                        v.muted = true;
                        v.play().catch(() => { /* playback blocked entirely; poster remains */ });
                      });
                return;
              }
              v.play().catch(() => {});
            } else {
              v.pause();
            }
          });
        });
      },
      { threshold: 0.25 }
    );
    io.observe(root);
    return () => io.disconnect();
  }, [soundOn]);

  const toggleSound = () => {
    const v = finalRef.current;
    if (!v) return;
    const next = !soundOn;
    v.muted = !next;
    if (next && v.paused) v.play().catch(() => {});
    setSoundOn(next);
    try { sessionStorage.setItem(SOUND_PREF_KEY, next ? "1" : "0"); } catch { /* storage unavailable in private mode */ }
  };

  return (
    <section ref={sectionRef} className="border-t border-bnm" data-testid="home-craft">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col items-center text-center">
          <Reveal className="eyebrow eyebrow-denim mb-5 md:mb-6">
            Behind the Craft
          </Reveal>
          <Reveal as="h2" delay={80} className="type-h2 max-w-[720px]">
            From set to <span className="font-serif-italic font-light">screen</span>.
          </Reveal>

          <Reveal delay={180} className="w-full flex justify-center">
            <div
              className="mt-10 md:mt-14 grid grid-cols-2 gap-3 sm:gap-5 md:gap-8 w-full max-w-[420px] sm:max-w-[560px] md:max-w-[780px]"
              data-testid="craft-frame"
            >
              {FILMS.map((f) => (
                <figure key={f.key} className="flex flex-col min-w-0">
                  <div className="relative">
                    <video
                      ref={f.hasSound ? finalRef : undefined}
                      poster={f.poster}
                      muted
                      loop
                      playsInline
                      autoPlay
                      preload="metadata"
                      aria-label={f.alt}
                      className="w-full aspect-[9/16] object-cover rounded-2xl md:rounded-3xl bg-[#f4f4f4] shadow-[0_24px_80px_rgba(10,10,10,0.12)]"
                      data-testid={`craft-video-${f.key}`}
                    >
                      <source src={f.webm} type="video/webm" />
                      <source src={f.mp4} type="video/mp4" />
                    </video>
                    {f.hasSound && (
                      <button
                        type="button"
                        onClick={toggleSound}
                        aria-label={soundOn ? "Mute the film" : "Play with sound"}
                        aria-pressed={soundOn}
                        className="absolute bottom-3 right-3 md:bottom-4 md:right-4 inline-flex items-center gap-2 rounded-full bg-black/45 backdrop-blur-[6px] text-white pl-2.5 pr-2.5 md:pl-3 md:pr-3.5 py-2 text-[11px] tracking-[0.05em] uppercase transition-all duration-300 hover:bg-black/60"
                        data-testid="craft-sound-toggle"
                      >
                        {soundOn ? (
                          <Volume2 className="w-3.5 h-3.5" aria-hidden />
                        ) : (
                          <VolumeX className="w-3.5 h-3.5" aria-hidden />
                        )}
                        {!soundOn && <span className="hidden md:inline">Tap for sound</span>}
                      </button>
                    )}
                  </div>
                  <figcaption className="mt-4 md:mt-5">
                    <div className="eyebrow eyebrow-denim text-[10px] sm:text-[11px]">{f.eyebrow}</div>
                    <div className="text-[12px] sm:text-[13.5px] text-muted-ink mt-1">{f.label}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>

          <Reveal delay={240}>
            <p
              className="mt-9 md:mt-12 text-muted-ink text-[10.5px] sm:text-[12px] md:text-[13px] tracking-[0.06em] md:tracking-[0.04em] uppercase whitespace-nowrap"
              data-testid="craft-caption"
            >
              Bluestone <span className="text-muted-ink/40 mx-1 sm:mx-1.5">•</span> Produced by Brand New Money Films
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default CraftFilm;
